import React, { useMemo, useRef, useState } from 'react';
import { TbAdjustments } from 'react-icons/tb';

import {
  Button,
  ButtonGroup,
  CheckboxList,
  Icon,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverFooter,
  PopoverTrigger,
  Portal,
  ScrollPane,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
} from '../../core-ui';
import { Tag } from '../../types/shared/Tag';
import { StatusText } from './VendorStatus';
import { TagLabelView } from './VendorTag';

type Props = {
  categories: Array<string>;
  selectedCategories: Set<string>;
  statuses: Array<string>;
  selectedStatuses: Set<string>;
  tags: Array<Tag>;
  selectedTags: Set<string>;
  onApply: (data: {
    categories: Set<string>;
    statuses: Set<string>;
    tags: Set<string>;
  }) => void;
  onReset: () => void;
};

export function FilterDropdown(props: Props) {
  const { categories, statuses, tags, onApply, onReset } = props;

  // Sort categories and tags (but not statuses)
  const sortedCategories = useMemo(() => {
    return categories.slice().sort((a, b) => a.localeCompare(b));
  }, [categories]);
  const sortedTags = useMemo(() => {
    return tags.slice().sort((a, b) => a.name.localeCompare(b.name));
  }, [tags]);

  const [isOpen, setOpen] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState(
    props.selectedCategories,
  );
  const [selectedStatuses, setSelectedStatuses] = useState(
    props.selectedStatuses,
  );
  const [selectedTags, setSelectedTags] = useState(props.selectedTags);
  const initialFocusRef = useRef<HTMLButtonElement>(null);
  return (
    <Popover
      initialFocusRef={initialFocusRef}
      isOpen={isOpen}
      onClose={() => setOpen(false)}
    >
      <PopoverTrigger>
        <Button
          leftIcon={<Icon as={TbAdjustments} />}
          onClick={() => {
            // Reset state before opening
            setSelectedCategories(props.selectedCategories);
            setSelectedStatuses(props.selectedStatuses);
            setSelectedTags(props.selectedTags);
            setOpen(true);
          }}
        >
          {t('Filters')}
        </Button>
      </PopoverTrigger>
      <Portal>
        <PopoverContent
          as="form"
          w="auto"
          maxW="90vw"
          onSubmit={(event) => {
            event.preventDefault();
            onApply({
              categories: selectedCategories,
              statuses: selectedStatuses,
              tags: selectedTags,
            });
            setOpen(false);
          }}
        >
          <PopoverArrow />
          <PopoverBody px={0} pb={0}>
            <ScrollPane
              key={
                // This resets the scroll position on open
                Number(isOpen)
              }
              maxH="250px"
              contentContainerProps={{ pb: 2 }}
            >
              <Table width="full" variant="unstyled">
                <Thead
                  position="sticky"
                  zIndex={1}
                  top={0}
                  bg="white"
                  boxShadow="0 1px var(--chakra-colors-gray-100)"
                >
                  <Tr>
                    <Th>{t('Category')}</Th>
                    <Th>{t('Status')}</Th>
                    <Th>{t('Tags')}</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  <Tr>
                    <Td verticalAlign="top">
                      <CheckboxList
                        items={sortedCategories}
                        getKey={(category) => category}
                        renderLabel={(category) => category}
                        selected={selectedCategories}
                        onSelectionChange={setSelectedCategories}
                      />
                    </Td>
                    <Td verticalAlign="top">
                      <CheckboxList
                        items={statuses}
                        getKey={(status) => status}
                        renderLabel={(status) => <StatusText status={status} />}
                        selected={selectedStatuses}
                        onSelectionChange={setSelectedStatuses}
                      />
                    </Td>
                    <Td verticalAlign="top">
                      <CheckboxList
                        items={sortedTags}
                        getKey={(tag) => tag.id}
                        renderLabel={(tag) => <TagLabelView tag={tag} />}
                        selected={selectedTags}
                        onSelectionChange={setSelectedTags}
                      />
                    </Td>
                  </Tr>
                </Tbody>
              </Table>
            </ScrollPane>
          </PopoverBody>
          <PopoverFooter display="flex" justifyContent="flex-end">
            <ButtonGroup justifyContent="flex-end">
              <Button
                onClick={() => {
                  onReset();
                  setOpen(false);
                }}
              >
                {t('Reset')}
              </Button>
              <Button type="submit" ref={initialFocusRef} colorScheme="blue">
                {t('Apply')}
              </Button>
            </ButtonGroup>
          </PopoverFooter>
        </PopoverContent>
      </Portal>
    </Popover>
  );
}
